<template>
  <div class="tab-content">
    <div class="group-summary">
      <p class="mb-1"><strong>{{ groupTitle }}</strong></p>
      <p class="mb-2">{{ groupDescription }}</p>
      <div class="url-tags">
        <a-tag
          v-for="url in group.urls"
          :key="group.mode + '-' + groupIndex + '-' + url.id"
          class="mb-1"
        >
          {{ url.url || url.webPageUrl || ('URL ID: ' + url.id) }}
        </a-tag>
      </div>
    </div>

    <div v-if="hasSimilarPolicies" class="similar-policies-section mt-3">
      <a-alert type="info" showIcon>
        <template slot="message">
          <strong>Similar domain found</strong>
        </template>
        <template slot="description">
          <p class="mb-2">
            Existing policies contain URLs with similar domains. You can add these URLs to an existing policy instead.
          </p>
          <div class="d-flex align-items-center" style="gap: 8px;">
            <a-select
              v-model="selectedSimilarPolicy"
              placeholder="Select an existing policy"
              style="min-width: 300px; flex: 1;"
              :options="similarPolicyOptions"
              allowClear
            />
            <a-button
              type="default"
              :disabled="!selectedSimilarPolicy"
              @click="onAddToExistingPolicy"
            >
              Add to Selected Policy
            </a-button>
          </div>
        </template>
      </a-alert>
    </div>

    <RulesTable
      v-if="group.mode === 'existing' && !editableRules"
      class="mt-3"
      title="Detected Rules"
      :rules="detectedRules"
      :expandKey="'detected-' + groupIndex"
      emptyMessage="No detected liveness rules found."
    />

    <RulesTable
      class="mt-3"
      :title="rulesTableTitle"
      :rules="sourceRules"
      :selectable="!editableRules"
      :editable="editableRules"
      :minTableWidth="rulesTableMinWidth"
      :groupByPrefix="editableRules"
      :ruleEdits="ruleEdits"
      :groupRuleEdits="groupRuleEdits"
      :groupKeyPrefix="String(groupIndex)"
      :selectedIndices="selectedIndices"
      :expandKey="(group.mode === 'existing' ? 'new-' : 'sugg-') + groupIndex"
      :emptyMessage="rulesEmptyMessage"
      @toggle="onToggle"
      @change="onRuleChange"
      @group-change="onGroupedRuleChange"
      @remove="onToggle"
    />

    <div class="actions mt-3">
      <a-button
        v-if="showApplyButton"
        class="mr-2"
        :disabled="isProceedDisabled"
        @click="$emit('proceed')"
      >
        {{ actionLabel }}
      </a-button>
      <a
        v-else
        :href="actionHref"
        class="ant-btn ant-btn-primary action-link-button"
        :class="{ 'ant-btn-disabled': isProceedDisabled, 'is-disabled': isProceedDisabled }"
        :aria-disabled="isProceedDisabled ? 'true' : 'false'"
        :tabindex="isProceedDisabled ? -1 : 0"
        role="button"
        @click="onActionClick"
        @auxclick="onActionClick"
      >
        <span>{{ actionLabel }}</span>
      </a>
      <a-button
        v-if="showApplyButton"
        type="primary"
        :loading="applying"
        :disabled="isActionDisabled"
        @click="$emit('apply')"
      >
        {{ applyLabel }}
      </a-button>
    </div>
  </div>
</template>

<script>
import RulesTable from './RulesTable.vue';
import {
  collapseSelectedRuleEntriesByCommonPrefix,
  countGroupedRules
} from '@shared/utils/policyRules';

function buildSelectionKey(groupIndex, rule, ruleIndex) {
  return `${groupIndex}-${rule.type}-${rule.field}-${ruleIndex}`;
}

export default {
  name: 'PolicyGroupContent',
  components: { RulesTable },
  props: {
    group: { type: Object, required: true },
    groupIndex: { type: Number, required: true },
    selectedRules: { type: Object, default: () => ({}) },
    similarPolicies: { type: Array, default: () => [] },
    actionHref: { type: String, default: '#' },
    editableRules: { type: Boolean, default: false },
    rulesTableMinWidth: { type: Number, default: 900 },
    ruleEdits: { type: Object, default: () => ({}) },
    groupRuleEdits: { type: Object, default: () => ({}) },
    showApplyButton: { type: Boolean, default: false },
    applying: { type: Boolean, default: false },
  },
  data() {
    return {
      selectedSimilarPolicy: null,
    };
  },
  computed: {
    groupTitle() {
      if (this.group.mode === 'do_not_monitor') {
        return 'Integrity engine — do not monitor';
      }
      if (this.group.mode === 'existing') {
        return `Existing Policy: ${this.group.policyName || `#${this.group.policyId}`}`;
      }
      return 'New Policy Suggestion';
    },
    groupDescription() {
      const urlsCount = (this.group.urls || []).length;
      if (this.group.mode === 'do_not_monitor') {
        return `${this.previewRuleCount} element(s) flagged to stop monitoring across ${urlsCount} URL${urlsCount === 1 ? '' : 's'}.`;
      }
      if (this.group.mode === 'existing') {
        const newCount = this.previewRuleCount;
        if (this.showApplyButton) {
          return `${urlsCount} URL(s) linked to this policy. ${newCount} new rule(s) suggested.`;
        }
        if (this.editableRules) {
          return `${urlsCount} URL(s) linked to this policy. ${newCount} new rule(s) to add.`;
        }
        return `${urlsCount} URL(s) linked to this policy. ${this.detectedRules.length} detected rule(s), ${newCount} new rule(s) to add.`;
      }
      if (this.showApplyButton) {
        return `${urlsCount} URL(s) do not have a policy. ${this.previewRuleCount} suggested rule(s) are available.`;
      }
      return `${urlsCount} URL(s) do not have a policy. ${this.previewRuleCount} suggested rule(s) will be pre-filled.`;
    },
    detectedRules() {
      return this.group.rules || [];
    },
    sourceRules() {
      return (
        this.group.mode === 'existing'
          ? this.group.newRulesOnly || []
          : this.group.rules || []
      );
    },
    selectedIndices() {
      const indices = new Set();
      this.sourceRules.forEach((rule, index) => {
        const key = this.getRuleKey(index);
        if (this.selectedRules[key] !== false) {
          indices.add(index);
        }
      });
      return indices;
    },
    actionLabel() {
      return this.group.mode === 'existing'
        ? 'Open Policy and Add Rules'
        : 'Create Policy with Suggested Rules';
    },
    isActionDisabled() {
      return this.selectedIndices.size === 0;
    },
    isProceedDisabled() {
      return this.isActionDisabled || this.applying;
    },
    rulesTableTitle() {
      if (this.group.mode === 'existing') return 'New Rules to Add';
      return this.editableRules ? 'Rule List' : 'Suggested Rules';
    },
    rulesEmptyMessage() {
      if (this.group.mode === 'existing') return 'No new rules to add to this policy.';
      if (this.group.mode === 'do_not_monitor') {
        return 'No integrity do-not-monitor rows for these URLs.';
      }
      return 'No suggested rules found.';
    },
    selectedRuleCount() {
      if (!this.editableRules) return this.selectedIndices.size;

      const entries = this.sourceRules.map((rule, ruleIndex) => ({
        rule,
        ruleIndex
      }));
      return collapseSelectedRuleEntriesByCommonPrefix(entries, {
        isSelected: (entry) => this.selectedIndices.has(entry.ruleIndex),
        getRuleEdit: (entry) => this.ruleEdits[this.getRuleKey(entry.ruleIndex)],
        getGroupEdit: (cluster) =>
          this.groupRuleEdits[`${this.groupIndex}-${cluster.groupId}`]
      }).length;
    },
    applyLabel() {
      const label = this.group.mode === 'do_not_monitor' ? 'item' : 'rule';
      return `Apply ${this.selectedRuleCount} ${label}(s)`;
    },
    previewRuleCount() {
      return this.editableRules
        ? countGroupedRules(this.sourceRules)
        : this.sourceRules.length;
    },
    hasSimilarPolicies() {
      return this.group.mode === 'new' && this.similarPolicies.length > 0;
    },
    similarPolicyOptions() {
      return this.similarPolicies.map(p => {
        const domains = Array.isArray(p.matchingDomains)
          ? p.matchingDomains
          : Array.from(p.matchingDomains || []);
        return {
          value: p.id,
          label: `${p.name || `Policy #${p.id}`} (${domains.join(', ')})`,
        };
      });
    },
  },
  methods: {
    getRuleKey(ruleIndex) {
      if (this.editableRules) return `${this.groupIndex}-${ruleIndex}`;
      const rules =
        this.group.mode === 'existing'
          ? this.group.newRulesOnly || []
          : this.group.rules || [];
      const rule = rules[ruleIndex] || {};
      return buildSelectionKey(this.groupIndex, rule, ruleIndex);
    },
    onToggle(ruleIndex) {
      this.$emit('toggle', ruleIndex);
    },
    onRuleChange(change) {
      this.$emit('change', change);
    },
    onGroupedRuleChange(change) {
      this.$emit('group-change', change);
    },
    onAddToExistingPolicy() {
      if (!this.selectedSimilarPolicy) return;
      this.$emit('proceedToExisting', this.selectedSimilarPolicy);
    },
    onActionClick(event) {
      if (this.isProceedDisabled) {
        event.preventDefault();
        return;
      }
      const isAuxClick = event.type === 'auxclick';
      const isMiddleClick = event.button === 1;
      const hasModifier = event.metaKey || event.ctrlKey || event.shiftKey;
      const openInNewTab = isAuxClick || isMiddleClick || hasModifier;

      if (isAuxClick && !isMiddleClick) {
        return;
      }

      this.$emit('proceed', { event, openInNewTab });

      if (!openInNewTab) {
        event.preventDefault();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.group-summary {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 4px;
  padding: 10px 12px;
}

.url-tags {
  max-height: 120px;
  overflow-y: auto;
}

.actions {
  display: flex;
  justify-content: flex-end;
}

.action-link-button {
  display: inline-flex;
  align-items: center;
  text-decoration: none;

  &.is-disabled {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.65;
  }

  &:hover,
  &:focus {
    text-decoration: none;
  }
}

.tab-content {
  max-width: 100%;
  overflow: hidden;
}

.similar-policies-section {
  ::v-deep .ant-alert-description {
    p {
      margin-bottom: 8px;
      color: rgba(0, 0, 0, 0.65);
    }
  }
}
</style>
